/* eslint-disable */

import {
  Avatar,
  Button,
  Form,
  Input,
  Modal,
  Radio,
  Result,
  Select,
  Spin,
  notification,
} from "antd";

const { Option } = Select;
import React, { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import { Send } from "lucide-react";
import useTransferValidation from "../../hooks/useTransferValidation";
import useTransferModal from "../../hooks/useTransferModal";
import { useSelector } from "react-redux";
import { showErrorNotification } from "../../common/utils/notification";

const TransferModal = ({
  onComplete,
  initialType = "bank",
  isBusiness = false,
  customTrigger,
  walletBalance,
  children,
}) => {
  const profileData = useSelector((state) => state.userProfile.dataFetched);
  const {
    validateTransferAmount,
    calculateTransferFees,
    getTierBenefits,
    apiConfig,
    transferConfig,
  } = useTransferValidation(profileData?.tier);
  const transferModalHook = useTransferModal(
    onComplete,
    initialType,
    isBusiness,
  );

  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [showTransaction, setShowTransaction] = useState(false);
  const [err_status, setErr_status] = useState(false);
  const [message, setMessage] = useState(false);

  const handleBeneficiaryChange = (value) => {
    const selected = transferModalHook.userBen.find(
      (beneficiary) => beneficiary.accountNumber === value,
    );
    transferModalHook.setSelectedBeneficiary(selected);
    transferModalHook.setBankAccountName(selected?.accountName);
    const selectedBenBank = transferModalHook.allBanks.find(
      (bank) => bank.code === selected.bankCode,
    );
    transferModalHook.setSelectedBank({
      key: selectedBenBank?.code,
      label: selectedBenBank?.name,
      title: selectedBenBank?.code,
      value: selectedBenBank?.code,
    });
    transferModalHook.setAccNo(selected?.accountNumber);
    transferModalHook.setBankBenDetails(true);
    form.setFieldsValue({
      accNo: selected?.accountNumber,
      accName: selected?.accountName,
      bank: selectedBenBank
        ? {
          key: selectedBenBank?.code,
          label: selectedBenBank?.name,
          title: selectedBenBank?.code,
          value: selectedBenBank?.code,
        }
        : undefined,
    });
  };

  const handleWalletBeneficiaryChange = (value) => {
    const selected = transferModalHook.userBen.find(
      (beneficiary) => beneficiary.accountNumber === value,
    );
    transferModalHook.setSelectedBeneficiary(selected);
    transferModalHook.setWalletAccountName(selected?.accountName);
    transferModalHook.setAccNo(selected?.accountNumber);
    transferModalHook.setWalletBenDetails(true);
    form.setFieldsValue({
      accNo: selected?.accountNumber,
      accName: selected?.accountName,
    });
  };

  const handleFormValuesChange = async (changedValues) => {
    const { bank, accNo, amount } = changedValues;

    if (bank) {
      transferModalHook.setSelectedBank(bank);
      transferModalHook.setSelectedBeneficiary(null);
      if (transferModalHook.accNo) {
        transferModalHook.verifyBankAccount(transferModalHook.accNo, bank);
      }
    }

    if (accNo) {
      transferModalHook.setAccNo(accNo);
      transferModalHook.setSelectedBeneficiary(null);
      const isValidLength =
        transferModalHook.transferType === "bank"
          ? accNo.length === 10
          : accNo.length === 8;

      if (isValidLength) {
        if (
          transferModalHook.transferType === "bank" &&
          transferModalHook.selectedBank
        ) {
          const data = await transferModalHook.verifyBankAccount(
            accNo,
            transferModalHook.selectedBank,
          );
          form.setFieldsValue({ accName: data?.accountName });
        } else if (transferModalHook.transferType === "wallet") {
          const data = await transferModalHook.verifyWalletAccount(accNo);
          form.setFieldsValue({ accName: data?.userName });
        }
      } else {
        transferModalHook.resetBankVerification();
        transferModalHook.resetWalletVerification();
        form.setFieldsValue({ accName: "" });
      }
    }

    if (amount && amount.length > 2) {
      transferModalHook.setLoadingCommission(true);
      try {
        const transferType =
          transferModalHook.transferType === "bank"
            ? "bankTransfer"
            : "konnectTransfer";
        const validation = validateTransferAmount(
          parseFloat(amount),
          transferType,
        );

        if (!validation.valid) {
          transferModalHook.setBankErrorMessage(validation.error);
          transferModalHook.setWalletErrorMessage(validation.error);
          transferModalHook.setLoadingCommission(false);
          return;
        }

        let fee = await calculateTransferFees(
          parseFloat(amount),
          transferType,
        );
        if (isBusiness) {
          fee = { fee: 0, total: parseFloat(amount), feePercentage: 0 };
        }
        const actualFee = fee?.fee || fee || 0;
        const totalDebit = parseFloat(amount) + actualFee;

        if (walletBalance !== undefined && totalDebit > parseFloat(walletBalance)) {
          transferModalHook.setBankErrorMessage("Insufficient wallet balance");
          transferModalHook.setWalletErrorMessage("Insufficient wallet balance");
          transferModalHook.setNormalCommission(fee);
          transferModalHook.setTotalCommission(totalDebit);
          transferModalHook.setLoadingCommission(false);
          return;
        }

        transferModalHook.setNormalCommission(fee);
        transferModalHook.setTotalCommission(totalDebit);
        transferModalHook.setBankErrorMessage(false);
        transferModalHook.setWalletErrorMessage(false);
      } catch (err) {
        transferModalHook.setBankErrorMessage("Failed to calculate fees");
        transferModalHook.setWalletErrorMessage("Failed to calculate fees");
      } finally {
        transferModalHook.setLoadingCommission(false);
      }
    }
  };

  useEffect(() => {
    if (
      (transferModalHook.fundOptions === "wallet" &&
        transferModalHook.transferTo === "konnect_balance") ||
      (transferModalHook.fundOptions === "konnect" &&
        transferModalHook.transferTo === "wallet_balance")
    ) {
      form.setFieldsValue({
        accNo: profileData?.accountId,
      });
      transferModalHook.setAccNo(profileData?.accountId);
      transferModalHook.verifyWalletAccount(profileData.accountId);
    }
  }, [
    transferModalHook.fundOptions,
    transferModalHook.transferTo,
    transferModalHook,
    profileData?.accountId,
    form,
  ]);

  const handleBankTransfer = async (values) => {
    const validation = validateTransferAmount(values?.amount, "bankTransfer");
    if (!validation.valid) {
      showErrorNotification(validation.error);
      return;
    }
    const result = await transferModalHook.transferToBank(
      values,
      transferModalHook.selectedBank,
      transferModalHook.accNo,
    );
    if (result?.data?.success || result?.success) {
      form.resetFields();
      transferModalHook.setTransferTo("");
      transferModalHook.setFundOptions("wallet");
      transferModalHook.resetBankVerification();
      transferModalHook.setKonnectTransferModal(false);
      transferModalHook.setTransferType("bank");
      transferModalHook.setPayout(false);
      transferModalHook.setTransferInfo({});
      transferModalHook.setNormalCommission(0);
    }
  };

  const handleWalletTransfer = async (values) => {
    const validation = validateTransferAmount(values?.amount, "konnectTransfer");
    if (!validation.valid) {
      showErrorNotification(validation.error);
      return;
    }
    if (
      transferModalHook.fundOptions === "konnect" &&
      transferModalHook.transferTo === "wallet_balance"
    ) {
      transferModalHook.setKonnectTransferModal(true);
      transferModalHook.setTransferInfo(values);
    } else {
      try {
        const result = await transferModalHook.transferToWallet(
          values,
          transferModalHook.accNo,
        );
        transferModalHook.setFundOptions("wallet");
        transferModalHook.resetWalletVerification();
        transferModalHook.setKonnectTransferModal(false);
        transferModalHook.setTransferType("bank");
        form.resetFields();
        transferModalHook.setTransferTo("");
        transferModalHook.setPayout(false);
        transferModalHook.setTransferInfo({});
        transferModalHook.setNormalCommission(0);
        transferModalHook.closePayout();
      } catch (error) {
        showErrorNotification(
          error?.response?.data?.message ||
          error?.message ||
          "Transfer to wallet failed",
        );
      }
    }
  };

  const handlePinVerification = async (values) => {
    await transferModalHook.verifyPin(values.pin);
  };

  const handleTransferTypeChange = (e) => {
    transferModalHook.setTransferType(e.target.value);
    form.resetFields();
    transferModalHook.setSelectedBeneficiary(null);
    transferModalHook.setTransferTo("");
    transferModalHook.resetBankVerification();
    transferModalHook.resetWalletVerification();
    transferModalHook.setTransferInfo({});
    transferModalHook.setNormalCommission(0);
    transferModalHook.setWalletTransferTarget("WALLET");
  };

  // Check if userBen is an array
  const isArrayUserBen = Array.isArray(transferModalHook.userBen);

  // Update the form value when normalCommission changes
  useEffect(() => {
    form.setFieldsValue({ fee: transferModalHook.normalCommission?.fee });
  }, [transferModalHook.normalCommission, form]);

  return (
    <>
      {children ? (
        <div
          onClick={() => {
            transferModalHook.setPayout(!transferModalHook.payOut);
          }}
        >
          {children}
        </div>
      ) : (
        <div
          className="card-action-box"
          onClick={() => {
            transferModalHook.setPayout(!transferModalHook.payOut);
          }}
        >
          <div className="bg-green-50 p-2 rounded-lg mb-2">
            <Send className="text-green-600 mx-auto text-center" size={24} />
          </div>
          <p>Pay Someone</p>
        </div>
      )}
      <Modal
        centered={true}
        title=""
        open={transferModalHook.payOut}
        footer={null}
        onCancel={() => {
          transferModalHook.setPayout(false);
          transferModalHook.closePayout();
        }}
        className="pb-5 pt-5"
      >
        {profileData && transferModalHook.verifiedPin ? (
          <div className="h-[400px] overflow-y-auto">
            <h2 className="text-5xl mb-5">Transfer to bank & Konnect wallet</h2>
            {walletBalance !== undefined && (
              <div className="mb-5 p-4 bg-green-50 rounded-xl border border-green-200">
                <span className="text-gray-600 text-xl font-medium block">Delivery Wallet Balance</span>
                <span className="text-4xl font-bold text-green-700 block mt-1">
                  ₦{parseFloat(walletBalance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}
            <div className="mb-5 rounded-lg">
              {/* <p className='text-2xl font-bold mb-2'>
                Your Tier:{' '}
                {profileData?.tier?.replace('tier_', '').toUpperCase() || '1'}
              </p> */}
              {/* <p className='text-2xl mb-2'>
                Bank Transfer Fee: {getTierBenefits().isTier2 ? apiConfig?.data?.bank?.tier2Fee ?? '0' : apiConfig?.data?.bank?.tier1Fee  ?? '0'}%
              </p> */}
              {/* <p className='text-2xl'>K2K Transfer Fee: 0% (Always free)</p> */}
              {!getTierBenefits().isTier2 &&
                !isBusiness &&
                !profileData?.isRestaurantOwner &&
                !profileData?.isBiker &&
                !profileData?.is_driver && (
                  <p className="text-2xl text-gray-600 mt-2">
                    Upgrade to Tier 2 for zero bank transfer fees and more
                    benefits!
                  </p>
                )}
            </div>
            {(transferModalHook.bankErrorMessage ||
              transferModalHook.walletErrorMessage ||
              transferModalHook.transferErrorMessage) && (
                <p className="text-red-500">
                  {transferModalHook.bankErrorMessage ||
                    transferModalHook.walletErrorMessage ||
                    transferModalHook.transferErrorMessage}
                </p>
              )}
            <div className="mb-2.5">
              <Radio.Group
                size="large"
                buttonStyle="solid"
                onChange={handleTransferTypeChange}
                defaultValue={transferModalHook.transferType}
              >
                <Radio.Button value="bank" className="m-1.5 rounded-xl">
                  To bank
                </Radio.Button>
                <Radio.Button value="wallet" className="m-1.5 rounded-xl">
                  To Konnect
                </Radio.Button>
              </Radio.Group>
            </div>
            {/* Bank Form */}
            {transferModalHook.transferType === "bank" ? (
              <>
                <Form
                  form={form}
                  onValuesChange={handleFormValuesChange}
                  onFinish={handleBankTransfer}
                  initialValues={{ transOpt: "wallet" }}
                  layout="vertical"
                >
                  {transferModalHook.loadingBen && (
                    <>
                      <label>Select Beneficiary</label>
                      <Spin size="small" />
                    </>
                  )}
                  {isArrayUserBen && !transferModalHook.loadingBen && (
                    <Form.Item name="beneficiary" label="Select Beneficiary">
                      <Select
                        placeholder="Select a beneficiary"
                        onChange={handleBeneficiaryChange}
                        value={
                          transferModalHook.selectedBeneficiary
                            ?.accountNumber || undefined
                        }
                        loading={transferModalHook.loadingBen}
                      >
                        {transferModalHook.userBen.map((beneficiary) => (
                          <Option
                            key={beneficiary.id}
                            value={beneficiary.accountNumber}
                          >
                            {beneficiary.accountName}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                  <Form.Item name="bank" label="Bank Name">
                    <Select
                      placeholder="Select Bank"
                      labelInValue
                      showSearch
                      optionFilterProp="value"
                      loading={transferModalHook.loadingBanks}
                    >
                      {transferModalHook.allBanks.map((bank) => (
                        <Select.Option
                          key={bank.code}
                          title={bank.code}
                          value={bank.name}
                        >
                          {bank.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <span className="pb-2.5 flex items-center justify-between">
                    <label>Account Number</label>
                    {transferModalHook.loadingBankDetails && (
                      <Button disabled loading type="text">
                        Loading
                      </Button>
                    )}
                  </span>
                  <Form.Item name="accNo">
                    <Input
                      type="number"
                      inputMode="numeric"
                      placeholder="Account Number"
                    />
                  </Form.Item>
                  <Form.Item name="accName" label="Account Name">
                    <Input
                      disabled
                      placeholder="Account Name"
                      value={transferModalHook.bankAccountName}
                    />
                  </Form.Item>
                  <span className="pb-2.5 flex items-center justify-between">
                    <label>Amount</label>
                    <div className="flex items-center gap-2">
                      {transferConfig?.bankTransfer?.minAmount && (
                        <span className="text-xs text-gray-500 font-medium">
                          Min: ₦{Number(transferConfig.bankTransfer.minAmount).toLocaleString()}
                        </span>
                      )}
                      {transferModalHook.loadingCommission && (
                        <Button disabled loading type="text">
                          Loading
                        </Button>
                      )}
                    </div>
                  </span>
                  <Form.Item
                    name="amount"
                    rules={[
                      { required: true, message: "Please enter an amount" },
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          const validation = validateTransferAmount(value, "bankTransfer");
                          if (!validation.valid) {
                            return Promise.reject(new Error(validation.error));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      inputMode="numeric"
                      placeholder={
                        transferConfig?.bankTransfer?.minAmount
                          ? `Min. ₦${Number(transferConfig.bankTransfer.minAmount).toLocaleString()}`
                          : "Amount"
                      }
                    />
                  </Form.Item>
                  {transferModalHook.bankErrorMessage && (
                    <div className="text-xs text-red-500 mb-2">
                      {transferModalHook.bankErrorMessage}
                    </div>
                  )}
                  <Form.Item label="Fee" name="fee">
                    <Input
                      disabled
                      placeholder="Fee"
                      value={transferModalHook.normalCommission}
                    />
                  </Form.Item>
                  <Form.Item name="narration" label="Narration">
                    <Input placeholder="Narration" />
                  </Form.Item>
                  <div className="flex justify-between">
                    <Button onClick={transferModalHook.closePayout}>
                      Cancel
                    </Button>
                    {transferModalHook.bankBenDetails &&
                      !transferModalHook.bankErrorMessage ? (
                      <>
                        <Button
                          htmlType="submit"
                          type="primary"
                          className="bg-black"
                        >
                          Proceed
                        </Button>
                      </>
                    ) : (
                      <Button disabled type="primary">
                        Proceed
                      </Button>
                    )}
                  </div>
                </Form>
              </>
            ) : (
              <>
                <Form
                  form={form}
                  onFinish={handleWalletTransfer}
                  onValuesChange={handleFormValuesChange}
                  initialValues={{ walletTransferTarget: "WALLET" }}
                  layout="vertical"
                >
                  <Form.Item
                    name="walletTransferTarget"
                    label="Transfer target"
                    initialValue="WALLET"
                  >
                    <Select
                      value={transferModalHook.walletTransferTarget}
                      onChange={(value) => {
                        transferModalHook.setWalletTransferTarget(value);
                        form.setFieldsValue({ walletTransferTarget: value });
                      }}
                      options={[
                        { value: "WALLET", label: "Wallet" },
                        { value: "FOURWARD", label: "4Ward" },
                      ]}
                    />
                  </Form.Item>
                  {transferModalHook.loadingBen && (
                    <>
                      <label>Select Beneficiary</label>
                      <Spin size="small" />
                    </>
                  )}
                  {isArrayUserBen && !transferModalHook.loadingBen && (
                    <Form.Item name="beneficiary" label="Select Beneficiary">
                      <Select
                        placeholder="Select a beneficiary"
                        onChange={handleWalletBeneficiaryChange}
                        value={
                          transferModalHook.selectedBeneficiary
                            ?.accountNumber || undefined
                        }
                        loading={transferModalHook.loadingBen}
                      >
                        {transferModalHook.userBen.map((beneficiary) => (
                          <Option
                            key={beneficiary.id}
                            value={beneficiary.accountNumber}
                          >
                            {beneficiary.accountName}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                  <span className="pb-2.5 flex items-center justify-between">
                    <label>Account ID</label>
                    {transferModalHook.loadingWalletDetails && (
                      <Button disabled loading type="text">
                        Loading
                      </Button>
                    )}
                  </span>
                  <Form.Item name="accNo">
                    <Input
                      type="number"
                      inputMode="numeric"
                      placeholder="Account ID"
                    />
                  </Form.Item>
                  <Form.Item name="accName" label="Account Name">
                    <Input
                      disabled
                      placeholder="Account Name"
                      value={transferModalHook.walletAccountName}
                    />
                  </Form.Item>
                  <span className="pb-2.5 flex items-center justify-between">
                    <label>Total Amount</label>
                    <div className="flex items-center gap-2">
                      {transferConfig?.konnectTransfer?.minAmount && (
                        <span className="text-xs text-gray-500 font-medium">
                          Min: ₦{Number(transferConfig.konnectTransfer.minAmount).toLocaleString()}
                        </span>
                      )}
                      {transferModalHook.loadingCommission && (
                        <Button disabled loading type="text">
                          Loading
                        </Button>
                      )}
                    </div>
                  </span>
                  <Form.Item
                    name="amount"
                    rules={[
                      { required: true, message: "Please enter an amount" },
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          const validation = validateTransferAmount(value, "konnectTransfer");
                          if (!validation.valid) {
                            return Promise.reject(new Error(validation.error));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      inputMode="numeric"
                      placeholder={
                        transferConfig?.konnectTransfer?.minAmount
                          ? `Min. ₦${Number(transferConfig.konnectTransfer.minAmount).toLocaleString()}`
                          : "Amount"
                      }
                    />
                  </Form.Item>
                  {transferModalHook.walletErrorMessage && (
                    <div className="text-xs text-red-500 mb-2">
                      {transferModalHook.walletErrorMessage}
                    </div>
                  )}
                  <Form.Item label="Fee" name="fee">
                    <Input
                      disabled
                      placeholder="Fee"
                      value={transferModalHook.normalCommission}
                    />
                  </Form.Item>
                  <Form.Item name="naration" label="Naration">
                    <Input placeholder="Naration" />
                  </Form.Item>
                  <div className="flex justify-between">
                    <Button onClick={transferModalHook.closePayout}>
                      Cancel
                    </Button>
                    {transferModalHook.walletBenDetails &&
                      !transferModalHook.walletErrorMessage ? (
                      <>
                        {transferModalHook.payingWallet ? (
                          <Button disabled loading type="primary">
                            Proceed
                          </Button>
                        ) : (
                          <Button
                            htmlType="submit"
                            type="primary"
                            className="bg-black"
                          >
                            Proceed
                          </Button>
                        )}
                      </>
                    ) : (
                      <Button disabled type="primary">
                        Proceed
                      </Button>
                    )}
                  </div>
                </Form>
              </>
            )}
          </div>
        ) : (
          <div>
            <h4 className="modal-title">Transaction Pin</h4>
            <p>
              Enter your transaction pin.{" "}
              <button
                className="font-gilroyBold text-blue-500"
                onClick={() => {
                  navigate(`/profile/trans-pin?pin=${true}`);
                }}
              >
                Create one here
              </button>
            </p>
            <div className="mt-[5%]"></div>
            {transferModalHook.revealPinMessage && (
              <p className="error-message">
                {transferModalHook.revealPinMessage}
              </p>
            )}
            <Form
              layout="vertical"
              style={{ maxWidth: 600 }}
              onFinish={handlePinVerification}
              autoComplete="off"
            >
              <Form.Item name="pin">
                <Input.Password
                  type="number"
                  inputMode="numeric"
                  placeholder="Enter Pin"
                  autoComplete="off"
                />
              </Form.Item>
              <div className="flex justify-between items-center">
                {transferModalHook.verifingPin ? (
                  <Button type="primary" loading disabled>
                    Verifing Pin
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    className="bg-green-700"
                    htmlType="submit"
                  >
                    Verify PIN
                  </Button>
                )}
                <Button onClick={() => transferModalHook.setPayout(false)}>
                  Cancel
                </Button>
              </div>
            </Form>
          </div>
        )}
      </Modal>
      {showTransaction && (
        <div className="w-full h-screen absolute top-0 left-0 bg-black bg-opacity-20 flex">
          <div className="w-fit bg-white m-auto z-10">
            {err_status ? (
              <Result
                status="success"
                title={message}
                subTitle=""
                extra={[
                  <Button key="buy" onClick={() => setShowTransaction(false)}>
                    Ok
                  </Button>,
                ]}
              />
            ) : (
              <Result
                status="success"
                title={message}
                subTitle=""
                extra={[
                  <Button key="buy" onClick={() => setShowTransaction(false)}>
                    Ok
                  </Button>,
                ]}
              />
            )}
          </div>
        </div>
      )}
      <Modal
        centered={true}
        open={transferModalHook.konnectTransferModal}
        onCancel={() => transferModalHook.setKonnectTransferModal(false)}
        footer={null}
      >
        {transferModalHook.transferErrorMessage && (
          <p className="text-red-500">
            {transferModalHook.transferErrorMessage}
          </p>
        )}
        <h2>Note, Konnect balance is primarily to pay for Konnect products.</h2>
        <p>Transfer to wallet may take up to 14 working days.</p>
        <p>
          To complete the transfer & credit within 14 days, <b>click confirm</b>
        </p>
        <div className="flex justify-between items-center mt-2.5">
          <Button
            onClick={() => transferModalHook.setKonnectTransferModal(false)}
          >
            Cancel
          </Button>
          {transferModalHook.transferErrorMessage ? (
            <Button type="primary" disabled>
              Confirm
            </Button>
          ) : (
            <>
              {transferModalHook.payingWallet ? (
                <Button disabled loading type="primary">
                  Proceed
                </Button>
              ) : (
                <Button
                  type="primary"
                  onClick={() =>
                    handleWalletTransfer(transferModalHook.transferInfo)
                  }
                >
                  Confirm
                </Button>
              )}
            </>
          )}
        </div>
      </Modal>
    </>
  );
};
export default TransferModal;
